// src/router/index.ts
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { adminRoutes } from './admin'

import AdminLoginView from '../views/admin/AdminLoginView.vue'
import LearnDashboardView from '../views/learn/LearnDashboardView.vue'
import { useAuthStore } from '../stores/auth'
import { usePermissionGuardStore } from '../stores/permissionGuard'
import { useUiStore } from '../stores/ui'
import { ROLE_GROUPS } from '../constants/roleGroups'

// lazy-loaded auth views
const ForgotPasswordView = () => import('../views/auth/ForgotPasswordView.vue')
const ResetPasswordView = () => import('../views/auth/ResetPasswordView.vue')
const SessionExpiredView = () => import('../views/auth/SessionExpiredView.vue')
const VerifyEmailView = () => import('../views/admin/system/VerifyEmailView.vue')
const TenantAccessActionView = () => import('../views/public/TenantAccessActionView.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/admin/login',
    name: 'admin-login',
    component: AdminLoginView,
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPasswordView,
    meta: { guestOnly: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordView,
    meta: { guestOnly: true },
  },
  {
    path: '/session-expired',
    name: 'session-expired',
    component: SessionExpiredView,
    meta: { public: true },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: VerifyEmailView,
    meta: { public: true },
  },
  {
    path: '/tenant-admin-access/:action(approve|reject)/:token',
    name: 'tenant-admin-access-action',
    component: TenantAccessActionView,
    meta: { public: true },
  },
  {
    path: '/tenant-admin-access/approve/:token',
    redirect: (to) => ({
      name: 'tenant-admin-access-action',
      params: { action: 'approve', token: String(to.params.token || '') },
    }),
    meta: { public: true },
  },
  {
    path: '/tenant-admin-access/reject/:token',
    redirect: (to) => ({
      name: 'tenant-admin-access-action',
      params: { action: 'reject', token: String(to.params.token || '') },
    }),
    meta: { public: true },
  },
  {
    path: '/learn/dashboard',
    name: 'learn-dashboard',
    component: LearnDashboardView,
    meta: { requiresAuth: true },
  },
  {
    path: '/',
    redirect: '/admin/login',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes: [adminRoutes, ...routes], // ✅ THIS is the missing piece
})

// Guards
router.beforeEach(async (to, _from, next) => {
  const auth = useAuthStore()
  const permissionGuard = usePermissionGuardStore()
  const ui = useUiStore()

  const isPublic = to.meta.public === true
  const requiresAuth = to.meta.requiresAuth === true
  const requiredPermission = (to.meta.requiredPermission as string | undefined) || ''
  const requiredRoles = (to.meta.requireRoles as string[] | undefined) || []
  const requiredPermissions = (to.meta.requirePermissions as string[] | undefined) || []
  const requiredAnyPermissions = (to.meta.requireAnyPermissions as string[] | undefined) || []
  const orAnyPermissions = (to.meta.orAnyPermissions as string[] | undefined) || []
  const isSuperAdmin = Boolean(auth.user?.is_super_admin)

  const denyAccess = (redirectName?: string | symbol) => {
    ui.showToast('You are not eligible to access this section.', 'warning')
    // Never redirect to the route that was just denied: that loops forever and freezes the app.
    if (redirectName && redirectName === to.name) {
      if (_from.matched.length) return next(false)
      auth.logout()
      return next({ name: 'admin-login' })
    }
    if (redirectName) {
      return next({ name: redirectName as any })
    }
    return next(false)
  }

  if (isPublic) {
    if (to.name === 'admin-login' && auth.isAuthenticated && auth.isAdmin) {
      const requestedRedirect = String(to.query.redirect || '').trim()
      if (requestedRedirect.startsWith('/') && !requestedRedirect.startsWith('//')) {
        return next(requestedRedirect)
      }
      return next({ name: 'admin.dashboard' })
    }
    return next()
  }

  if (requiresAuth && !auth.isAuthenticated) {
    return next({ name: 'admin-login' })
  }

  if (auth.isAuthenticated) {
    if (!permissionGuard.loaded) {
      permissionGuard.syncFromAuth()
      await permissionGuard.loadResolvedPermissions()
    }
  } else {
    permissionGuard.reset()
  }

  if (requiredRoles.length > 0) {
    // The admin shell gate (MANAGE_PANEL) also admits permission-scoped admin-app roles
    // such as admin_billing_manager; their pages are gated by requireAnyPermissions.
    const isShellGate = requiredRoles === ROLE_GROUPS.MANAGE_PANEL
    const hasAnyRequiredRole =
      isSuperAdmin ||
      requiredRoles.some((code) => auth.hasRole(code)) ||
      (isShellGate && auth.hasAdminAppRole) ||
      (orAnyPermissions.length > 0 && permissionGuard.hasAnyPermission(orAnyPermissions))
    if (!hasAnyRequiredRole) {
      return denyAccess('admin.dashboard')
    }
  }

  if (requiredPermission) {
    if (!permissionGuard.hasPermission(requiredPermission)) {
      return denyAccess('admin.dashboard')
    }
  }

  if (requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every((code) => permissionGuard.hasPermission(code))
    if (!hasAllPermissions) {
      return denyAccess('admin.dashboard')
    }
  }

  if (requiredAnyPermissions.length > 0) {
    const hasAnyPermission = permissionGuard.hasAnyPermission(requiredAnyPermissions)
    if (!hasAnyPermission) {
      return denyAccess('admin.dashboard')
    }
  }

  return next()
})

export default router
